/**
 * PM Spec 17/02/2026 — AdvancesManager
 *
 * Modale de gestion des acomptes pré-facturation.
 * Ouverte depuis la fiche admin opération.
 * Liste les acomptes, permet d'en ajouter, télécharger leur reçu PDF,
 * et supprimer (uniquement avant émission de la facture).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Loader2, Plus, Trash2, FileText, Fuel, Receipt, MapPin, Wallet, AlertTriangle, History, Mail, ArrowDown, ArrowUp, RefreshCw, Send, Copy, Ban, CheckCircle2, XCircle, Info } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { CreatePaymentRequestDialog, NoWaveDialog } from '../pages/admin/WavePaymentsPage';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_OPTIONS = [
  { value: 'fuel', label: 'Carburant', icon: Fuel, color: 'text-orange-600' },
  { value: 'road_fees', label: 'Frais de route', icon: MapPin, color: 'text-blue-600' },
  { value: 'general_prefinancing', label: 'Préfinancement général', icon: Wallet, color: 'text-purple-600' },
  { value: 'acompte', label: 'Acompte', icon: Receipt, color: 'text-emerald-600' },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: 'wave', label: 'Wave' },
  { value: 'bank_transfer', label: 'Virement bancaire' },
  { value: 'cash', label: 'Espèces' },
  { value: 'mobile_money', label: 'Mobile Money' },
  { value: 'check', label: 'Chèque' },
];

const fmtAmount = (n) => Number(n || 0).toLocaleString('fr-FR') + ' XOF';
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

const AdvancesManager = ({ open, onOpenChange, operation, onAdvancesChange }) => {
  const { toast } = useToast();
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  // PM Spec 19/05/2026 — modifications post-émission autorisées avec motif
  const [reason, setReason] = useState('');
  const [pendingRegeneration, setPendingRegeneration] = useState(false);
  // PM Spec 19/05/2026 — journal d'audit + envoi email
  const [activeTab, setActiveTab] = useState('manage');
  const [auditTimeline, setAuditTimeline] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [lastRegenSuccess, setLastRegenSuccess] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  // PM Spec 22/02/2026 — Onglet Wave (Payment Requests)
  const [wavePRs, setWavePRs] = useState([]);
  const [waveLoading, setWaveLoading] = useState(false);
  const [showCreatePRDialog, setShowCreatePRDialog] = useState(false);
  const [showNoWaveDialog, setShowNoWaveDialog] = useState(false);
  const [form, setForm] = useState({
    type: 'fuel',
    label: '',
    amount: '',
    paymentMethod: 'wave',
    reference: '',
    notes: '',
  });

  const operationId = operation?.id;
  const alreadyInvoiced = !!operation?.invoiceNumber;
  const invoiceStatus = (operation?.invoiceStatus || '').toLowerCase();
  const isPaid = invoiceStatus === 'paid' || invoiceStatus === 'payee';
  const opTotal = useMemo(
    () => Number(operation?.montantTotalTTC || operation?.invoiceAmount || operation?.finalPrice || operation?.estimatedPrice || 0),
    [operation]
  );

  const totalAdvances = useMemo(
    () => advances.reduce((sum, a) => sum + Number(a.amount || 0), 0),
    [advances]
  );

  const fetchAdvances = useCallback(async () => {
    if (!operationId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/financial/${operationId}/advances`, {
      });
      setAdvances(res.data.advances || []);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Impossible de charger les acomptes',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [operationId, toast]);

  useEffect(() => {
    if (open) {
      fetchAdvances();
      setShowAddForm(false);
      setReason('');
      setPendingRegeneration(false);
    }
  }, [open, fetchAdvances]);

  const requiresReason = alreadyInvoiced; // motif obligatoire dès la facture émise

  const handleAdd = async () => {
    if (!form.label.trim()) {
      toast({ title: 'Champ requis', description: 'Le libellé est obligatoire', variant: 'destructive' });
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      toast({ title: 'Champ requis', description: 'Le montant doit être supérieur à 0', variant: 'destructive' });
      return;
    }
    if (requiresReason && !reason.trim()) {
      toast({
        title: 'Motif requis',
        description: 'La facture est déjà émise. Indiquez le motif de cette modification (audit OHADA).',
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/api/financial/${operationId}/advances`,
        {
          type: form.type,
          label: form.label.trim(),
          amount: Number(form.amount),
          paymentMethod: form.paymentMethod,
          reference: form.reference.trim() || null,
          notes: form.notes.trim() || null,
          reason: requiresReason ? reason.trim() : null,
        },
        {}
      );
      toast({ title: 'Acompte ajouté', description: 'Reçu d\'acompte généré.' });
      setForm({ type: 'fuel', label: '', amount: '', paymentMethod: 'wave', reference: '', notes: '' });
      setShowAddForm(false);
      if (res.data?.requiresInvoiceRegeneration) {
        setPendingRegeneration(true);
      }
      await fetchAdvances();
      onAdvancesChange?.();
    } catch (err) {
      toast({
        title: 'Impossible d\'ajouter l\'acompte',
        description: err.response?.data?.detail || 'Erreur serveur',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (adv) => {
    if (!window.confirm(`Supprimer l'acompte « ${adv.label} » de ${fmtAmount(adv.amount)} ?`)) return;
    if (requiresReason && !reason.trim()) {
      toast({
        title: 'Motif requis',
        description: 'La facture est déjà émise. Indiquez d\'abord le motif de modification.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const url = `${API}/api/financial/${operationId}/advances/${adv.id}` +
        (requiresReason ? `?reason=${encodeURIComponent(reason.trim())}` : '');
      const res = await axios.delete(url, {});
      toast({ title: 'Acompte supprimé' });
      if (res.data?.requiresInvoiceRegeneration) {
        setPendingRegeneration(true);
      }
      await fetchAdvances();
      onAdvancesChange?.();
    } catch (err) {
      toast({
        title: 'Suppression impossible',
        description: err.response?.data?.detail || 'Erreur serveur',
        variant: 'destructive',
      });
    }
  };

  const handleRegenerateInvoice = async () => {
    if (!reason.trim()) {
      toast({
        title: 'Motif requis',
        description: 'Indiquez le motif de régénération (conservation audit OHADA).',
        variant: 'destructive',
      });
      return;
    }
    setRegenerating(true);
    try {
      const res = await axios.post(
        `${API}/api/financial/${operationId}/regenerate-invoice`,
        { reason: reason.trim() },
        {}
      );
      const newAmt = res.data?.newAmount || 0;
      toast({
        title: `Facture ${res.data?.invoiceNumber} régénérée`,
        description: `Nouveau net à recevoir : ${fmtAmount(newAmt)}`,
      });
      setPendingRegeneration(false);
      setLastRegenSuccess(true);
      // Rafraîchir le journal d'audit en arrière-plan
      fetchAuditLog();
      onAdvancesChange?.();
    } catch (err) {
      toast({
        title: 'Régénération impossible',
        description: err.response?.data?.detail || 'Erreur serveur',
        variant: 'destructive',
      });
    } finally {
      setRegenerating(false);
    }
  };

  const fetchWavePRs = useCallback(async () => {
    if (!operationId) return;
    setWaveLoading(true);
    try {
      const res = await axios.get(`${API}/api/payment-requests`, {
        params: { operation_id: operationId, limit: 100 },
      });
      setWavePRs(res.data?.items || []);
    } catch (err) {
      toast({
        title: 'Erreur chargement Wave',
        description: err.response?.data?.detail || 'Erreur serveur',
        variant: 'destructive',
      });
    } finally {
      setWaveLoading(false);
    }
  }, [operationId, toast]);

  const copyText = (text, label) => {
    try {
      navigator.clipboard.writeText(text);
      toast({ title: `✓ ${label} copié` });
    } catch { /* no-op */ }
  };

  const handleValidatePR = async (pr) => {
    if (!window.confirm(`Valider la demande ${pr.refPaiement} ?`)) return;
    try {
      await axios.post(`${API}/api/payment-requests/${pr.id}/validate`, { forceOverride: false });
      toast({ title: 'Demande validée' });
      fetchWavePRs();
    } catch (err) {
      const msg = err?.response?.data?.detail || '';
      if (msg.includes('4-yeux') && window.confirm(`${msg}\n\nForcer la validation (bypass 4-yeux) ?`)) {
        try {
          await axios.post(`${API}/api/payment-requests/${pr.id}/validate`, { forceOverride: true });
          toast({ title: 'Demande validée (bypass 4-yeux)' });
          fetchWavePRs();
        } catch (e2) {
          toast({ title: 'Erreur', description: e2?.response?.data?.detail, variant: 'destructive' });
        }
      } else {
        toast({ title: 'Erreur validation', description: msg, variant: 'destructive' });
      }
    }
  };

  const handleDeclarePaidQuick = async (pr) => {
    if (!window.confirm(
      `Marquer ${pr.refPaiement} comme payée dans Wave ?\n\nUne Advance de ${fmtAmount(pr.amount)} sera générée automatiquement.`
    )) return;
    try {
      await axios.post(`${API}/api/payment-requests/${pr.id}/declare-paid`, {});
      toast({ title: `${pr.refPaiement} déclarée payée`, description: 'Une Advance a été générée.' });
      fetchWavePRs();
      fetchAdvances();
      onAdvancesChange?.();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail, variant: 'destructive' });
    }
  };

  const handleCancelPR = async (pr) => {
    const reason = window.prompt(`Motif d'annulation pour ${pr.refPaiement} ?`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await axios.post(`${API}/api/payment-requests/${pr.id}/cancel`, { reason: reason.trim() });
      toast({ title: 'Demande annulée' });
      fetchWavePRs();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail, variant: 'destructive' });
    }
  };

  const fetchAuditLog = useCallback(async () => {
    if (!operationId) return;
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/api/financial/${operationId}/audit-log`, {});
      setAuditTimeline(res.data?.timeline || []);
    } catch (err) {
      toast({
        title: 'Impossible de charger le journal',
        description: err.response?.data?.detail || 'Erreur serveur',
        variant: 'destructive',
      });
    } finally {
      setAuditLoading(false);
    }
  }, [operationId, toast]);

  const handleSendInvoiceEmail = async () => {
    if (!window.confirm(
      `Envoyer la facture ${operation?.invoiceNumber} mise à jour au client par email ?`
    )) return;
    setEmailSending(true);
    try {
      const res = await axios.post(
        `${API}/api/financial/${operationId}/send-invoice-email`, {}, {}
      );
      const recipients = (res.data?.sentTo || []).join(', ') || 'le client';
      toast({
        title: 'Email envoyé',
        description: `Facture ${res.data?.invoiceNumber} envoyée à ${recipients}.`,
      });
      setLastRegenSuccess(false);
      // Rafraîchir audit
      if (activeTab === 'audit') fetchAuditLog();
    } catch (err) {
      toast({
        title: "Envoi de l'email impossible",
        description: err.response?.data?.detail || 'Erreur serveur',
        variant: 'destructive',
      });
    } finally {
      setEmailSending(false);
    }
  };

  const typeOption = (value) => TYPE_OPTIONS.find(t => t.value === value) || TYPE_OPTIONS[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="advances-manager">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-amber-600" />
            Gestion des acomptes / pré-financement
          </DialogTitle>
          <DialogDescription>
            Saisir les acomptes versés par le client (carburant, frais de route, etc.) avant la facturation.
            Ils seront défalqués automatiquement sur la facture finale.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Résumé en-tête */}
          <div className="grid grid-cols-3 gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
            <div>
              <div className="text-xs text-amber-700">Total opération (estimé)</div>
              <div className="font-bold text-amber-900 font-mono">{fmtAmount(opTotal)}</div>
            </div>
            <div>
              <div className="text-xs text-amber-700">Acomptes cumulés</div>
              <div className="font-bold text-amber-900 font-mono">– {fmtAmount(totalAdvances)}</div>
            </div>
            <div>
              <div className="text-xs text-green-700">Restant à recevoir</div>
              <div className="font-bold text-green-700 font-mono">{fmtAmount(Math.max(0, opTotal - totalAdvances))}</div>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={(v) => {
            setActiveTab(v);
            if (v === 'audit') fetchAuditLog();
            if (v === 'wave') fetchWavePRs();
          }}>
            <TabsList className="grid grid-cols-3 w-full" data-testid="advances-tabs">
              <TabsTrigger value="manage" data-testid="tab-manage">
                <Wallet className="w-3.5 h-3.5 mr-1.5" /> Acomptes
              </TabsTrigger>
              <TabsTrigger value="wave" data-testid="tab-wave">
                <Send className="w-3.5 h-3.5 mr-1.5" /> Paiements Wave
              </TabsTrigger>
              <TabsTrigger value="audit" data-testid="tab-audit">
                <History className="w-3.5 h-3.5 mr-1.5" /> Journal d&apos;audit
              </TabsTrigger>
            </TabsList>

            <TabsContent value="manage" className="space-y-4 pt-3">

          {alreadyInvoiced && (
            <div className={`flex items-start gap-2 rounded p-3 text-sm border ${
              isPaid ? 'bg-red-50 border-red-300 text-red-900' : 'bg-orange-50 border-orange-300 text-orange-900'
            }`} data-testid="advances-warning-banner">
              <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isPaid ? 'text-red-600' : 'text-orange-600'}`} />
              <div className="space-y-1">
                <div>
                  La facture <strong>{operation.invoiceNumber}</strong> {isPaid ? 'a déjà été ' : 'a été '}<strong>{isPaid ? 'PAYÉE' : 'émise'}</strong>.
                  {isPaid
                    ? ' Toute modification décalera le cash encaissé — saisissez impérativement un motif clair.'
                    : ' Vos modifications déclencheront sa régénération automatique avec audit OHADA.'}
                </div>
                <div className="pt-2">
                  <Label className="text-xs font-semibold">
                    Motif de modification <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Ex: Acompte carburant complémentaire versé par le chauffeur le 18/05"
                    className="mt-1 bg-white"
                    data-testid="advances-reason-input"
                  />
                  <div className="text-[11px] text-slate-500 mt-1">
                    Sera conservé dans le journal d&apos;audit OHADA Art.17 (immuable).
                  </div>
                </div>
              </div>
            </div>
          )}

          {pendingRegeneration && (
            <div className="flex items-center justify-between bg-amber-100 border border-amber-300 rounded p-3 text-sm" data-testid="advances-regen-banner">
              <div className="text-amber-900 flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                Les acomptes ont changé. La facture <strong>{operation?.invoiceNumber}</strong> doit être régénérée pour refléter le nouveau montant net.
              </div>
              <Button
                onClick={handleRegenerateInvoice}
                disabled={regenerating || !reason.trim()}
                className="bg-amber-700 hover:bg-amber-800 text-white"
                data-testid="advances-regen-btn"
              >
                {regenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
                Régénérer la facture
              </Button>
            </div>
          )}

          {/* Liste */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
            </div>
          ) : advances.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              Aucun acompte enregistré pour cette opération.
            </div>
          ) : (
            <div className="space-y-2">
              {advances.map((adv) => {
                const opt = typeOption(adv.type);
                const Icon = opt.icon;
                return (
                  <div
                    key={adv.id}
                    className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3 hover:border-amber-300 transition"
                    data-testid={`advance-row-${adv.id}`}
                  >
                    <Icon className={`w-5 h-5 ${opt.color} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{adv.label}</span>
                        <Badge variant="outline" className="text-[10px]">{opt.label}</Badge>
                        <Badge className="bg-slate-100 text-slate-700 border-slate-300 text-[10px] font-mono">{adv.receiptNumber}</Badge>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {PAYMENT_METHOD_OPTIONS.find(p => p.value === adv.paymentMethod)?.label || adv.paymentMethod}
                        {adv.reference && <> • Réf. {adv.reference}</>}
                        <> • {fmtDate(adv.paidAt)}</>
                      </div>
                      {adv.notes && <div className="text-xs text-slate-600 mt-1 italic">« {adv.notes} »</div>}
                    </div>
                    <div className="text-right">
                      <div className="font-bold font-mono text-amber-700">+{fmtAmount(adv.amount)}</div>
                      {adv.receiptPdfPath && (
                        <a
                          href={`${API}/api/documents/download/${adv.receiptNumber}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-1"
                          data-testid={`advance-pdf-${adv.id}`}
                        >
                          <FileText className="w-3 h-3" /> Reçu PDF
                        </a>
                      )}
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(adv)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`advance-delete-${adv.id}`}
                      title={alreadyInvoiced ? 'Suppression autorisée — motif requis ci-dessus' : 'Supprimer'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Formulaire ajout — toujours accessible, gardes via motif obligatoire */}
          <Separator />
          {!showAddForm ? (
            <Button
              onClick={() => setShowAddForm(true)}
              className="w-full bg-amber-600 hover:bg-amber-700"
              data-testid="advance-add-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter un acompte
              {alreadyInvoiced && <span className="ml-2 text-xs opacity-80">(motif requis)</span>}
            </Button>
          ) : (
            <div className="bg-amber-50/40 border border-amber-200 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-sm text-amber-900">Nouvel acompte</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Type *</Label>
                      <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                        <SelectTrigger data-testid="advance-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Mode de paiement *</Label>
                      <Select value={form.paymentMethod} onValueChange={(v) => setForm({ ...form, paymentMethod: v })}>
                        <SelectTrigger data-testid="advance-method-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PAYMENT_METHOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Libellé *</Label>
                    <Input
                      value={form.label}
                      onChange={(e) => setForm({ ...form, label: e.target.value })}
                      placeholder="Ex: Carburant Bouaké"
                      data-testid="advance-label-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Montant TTC (XOF) *</Label>
                      <Input
                        type="number"
                        min={0}
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        className="text-right font-mono"
                        data-testid="advance-amount-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Référence transaction</Label>
                      <Input
                        value={form.reference}
                        onChange={(e) => setForm({ ...form, reference: e.target.value })}
                        placeholder="Ex: TX-WAVE-12345"
                        data-testid="advance-ref-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Note (optionnel)</Label>
                    <Textarea
                      rows={2}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Ex: Versé par M. Kouadio le 15/02"
                      data-testid="advance-notes-input"
                    />
                  </div>
                  <div className="flex gap-2 justify-end pt-2">
                    <Button variant="outline" onClick={() => setShowAddForm(false)} disabled={saving}>
                      Annuler
                    </Button>
                    <Button
                      onClick={handleAdd}
                      disabled={saving || !form.label || !form.amount}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="advance-submit-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                      Enregistrer et générer le reçu
                    </Button>
                  </div>
                </div>
              )}

              {/* Bouton "Renvoyer email" affiché après une régénération réussie */}
              {lastRegenSuccess && alreadyInvoiced && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 flex items-center justify-between" data-testid="advances-email-banner">
                  <div className="text-sm text-blue-900 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-blue-600" />
                    Facture régénérée. Voulez-vous renvoyer la version mise à jour au client&nbsp;?
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setLastRegenSuccess(false)}
                      data-testid="advances-email-skip-btn"
                    >
                      Plus tard
                    </Button>
                    <Button
                      onClick={handleSendInvoiceEmail}
                      disabled={emailSending}
                      className="bg-blue-700 hover:bg-blue-800 text-white"
                      size="sm"
                      data-testid="advances-email-send-btn"
                    >
                      {emailSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      Renvoyer au client
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="wave" className="space-y-3 pt-3" data-testid="wave-payments-panel">
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs text-slate-600 flex-1">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Info className="w-3.5 h-3.5 text-emerald-600" />
                    <span className="font-semibold">Workflow paiement Wave</span>
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Créer une demande génère une référence <code className="font-mono">PAY-YYYYMM-NNNNN</code> à copier dans le commentaire Wave.
                    Elle sera automatiquement rapprochée lors de l&apos;import de l&apos;export Wave mensuel.
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => setShowCreatePRDialog(true)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="pr-open-create-btn"
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Nouvelle demande
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowNoWaveDialog(true)}
                    className="border-slate-300"
                    data-testid="pr-open-no-wave-btn"
                  >
                    <Ban className="w-3.5 h-3.5 mr-1.5" /> Non concernée Wave
                  </Button>
                </div>
              </div>

              {waveLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-emerald-600" /></div>
              ) : wavePRs.length === 0 ? (
                <div className="text-center py-8 text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg">
                  Aucune demande de paiement Wave pour cette opération.
                </div>
              ) : (
                <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                  {wavePRs.map((pr) => {
                    const statusColors = {
                      draft: 'bg-slate-100 text-slate-700 border-slate-200',
                      validated: 'bg-blue-100 text-blue-700 border-blue-200',
                      paid_declared: 'bg-amber-100 text-amber-700 border-amber-200',
                      reconciled: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                      cancelled: 'bg-zinc-100 text-zinc-500 border-zinc-200',
                    };
                    const statusLabel = {
                      draft: 'Brouillon',
                      validated: 'Validée',
                      paid_declared: 'Payée déclarée',
                      reconciled: 'Rapprochée',
                      cancelled: 'Annulée',
                    };
                    return (
                      <div
                        key={pr.id}
                        className="bg-white border border-slate-200 rounded-lg p-3 hover:border-emerald-300 transition"
                        data-testid={`pr-op-row-${pr.refPaiement}`}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs font-bold text-emerald-800">{pr.refPaiement}</span>
                          <Badge className={`text-[10px] font-semibold border ${statusColors[pr.status] || ''}`}>
                            {statusLabel[pr.status] || pr.status}
                          </Badge>
                          <Badge variant="outline" className="text-[10px] capitalize">{pr.type}</Badge>
                          <span className="ml-auto font-mono font-bold text-emerald-700">
                            {fmtAmount(pr.amount)}
                          </span>
                        </div>
                        <div className="text-xs text-slate-600 mt-1">
                          <span className="text-slate-500">Chauffeur :</span> {pr.driverName || '—'}
                          <span className="mx-1">·</span>
                          <span className="text-slate-500">Num Wave :</span>{' '}
                          <span className="font-mono">{pr.waveNumber || '—'}</span>
                        </div>
                        {pr.waveComment && (
                          <div className="mt-1 text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded px-2 py-1 font-mono flex items-center gap-2">
                            <span className="flex-1 truncate">{pr.waveComment}</span>
                            <button
                              onClick={() => copyText(pr.waveComment, 'Commentaire Wave')}
                              className="text-emerald-600 hover:text-emerald-800"
                              title="Copier le commentaire Wave"
                              data-testid={`pr-op-copy-comment-${pr.refPaiement}`}
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        {pr.warnings && pr.warnings.length > 0 && (
                          <div className="mt-1 text-[11px] text-amber-700 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> {pr.warnings[0]}
                          </div>
                        )}
                        <div className="mt-2 flex items-center gap-1 flex-wrap">
                          <button
                            onClick={() => copyText(pr.waveNumber || '', 'Numéro Wave')}
                            className="text-[10px] text-slate-500 hover:text-slate-800 px-1.5 py-0.5 border border-slate-200 rounded"
                            data-testid={`pr-op-copy-num-${pr.refPaiement}`}
                          >
                            <Copy className="w-2.5 h-2.5 inline mr-0.5" /> Numéro
                          </button>
                          <button
                            onClick={() => copyText(String(pr.amount), 'Montant')}
                            className="text-[10px] text-slate-500 hover:text-slate-800 px-1.5 py-0.5 border border-slate-200 rounded"
                            data-testid={`pr-op-copy-amt-${pr.refPaiement}`}
                          >
                            <Copy className="w-2.5 h-2.5 inline mr-0.5" /> Montant
                          </button>
                          {pr.status === 'draft' && (
                            <button
                              onClick={() => handleValidatePR(pr)}
                              className="text-[10px] text-blue-700 hover:text-blue-900 font-semibold px-1.5 py-0.5 border border-blue-200 rounded bg-blue-50"
                              data-testid={`pr-op-validate-${pr.refPaiement}`}
                            >
                              <CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5" /> Valider
                            </button>
                          )}
                          {(pr.status === 'draft' || pr.status === 'validated') && (
                            <button
                              onClick={() => handleDeclarePaidQuick(pr)}
                              className="text-[10px] text-emerald-700 hover:text-emerald-900 font-semibold px-1.5 py-0.5 border border-emerald-200 rounded bg-emerald-50"
                              data-testid={`pr-op-pay-${pr.refPaiement}`}
                            >
                              <CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5" /> Marquer payée
                            </button>
                          )}
                          {pr.status !== 'reconciled' && pr.status !== 'cancelled' && (
                            <button
                              onClick={() => handleCancelPR(pr)}
                              className="text-[10px] text-rose-600 hover:text-rose-800 px-1.5 py-0.5 border border-rose-200 rounded"
                              data-testid={`pr-op-cancel-${pr.refPaiement}`}
                            >
                              <XCircle className="w-2.5 h-2.5 inline mr-0.5" /> Annuler
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="audit" className="space-y-3 pt-3" data-testid="audit-timeline-panel">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  Journal immuable OHADA Art.17 — toutes les modifications conservées.
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchAuditLog}
                  disabled={auditLoading}
                  data-testid="audit-refresh-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${auditLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              {auditLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>
              ) : auditTimeline.length === 0 ? (
                <div className="text-center py-8 text-sm text-slate-500">
                  Aucune entrée d&apos;audit pour cette opération.
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                  {auditTimeline.map((entry, idx) => {
                    const isAdvance = entry.kind === 'advance';
                    const isAdd = entry.action === 'add';
                    return (
                      <div
                        key={idx}
                        className={`border rounded-lg p-3 text-sm ${
                          isAdvance
                            ? (isAdd ? 'border-emerald-200 bg-emerald-50/40' : 'border-red-200 bg-red-50/40')
                            : 'border-amber-300 bg-amber-50/60'
                        }`}
                        data-testid={`audit-entry-${idx}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="mt-0.5">
                            {isAdvance ? (
                              isAdd ? <ArrowUp className="w-4 h-4 text-emerald-600" /> : <ArrowDown className="w-4 h-4 text-red-600" />
                            ) : (
                              <Receipt className="w-4 h-4 text-amber-700" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold">
                                {isAdvance
                                  ? (isAdd ? 'Acompte ajouté' : 'Acompte supprimé')
                                  : `Facture ${entry.invoiceNumber || ''} régénérée`}
                              </span>
                              {isAdvance && entry.afterInvoiceEmitted && (
                                <Badge className="bg-orange-100 text-orange-800 border-orange-300 text-[10px]">
                                  Post-facture
                                </Badge>
                              )}
                            </div>
                            {isAdvance ? (
                              <div className="text-xs text-slate-700 mt-1">
                                <span className="font-mono">{fmtAmount(entry.amount)}</span>
                                {entry.label && <> · {entry.label}</>}
                              </div>
                            ) : (
                              <div className="text-xs text-slate-700 mt-1 font-mono">
                                {fmtAmount(entry.previousAmount)} → {fmtAmount(entry.newAmount)}
                                <span className="text-slate-500"> (acomptes&nbsp;: {fmtAmount(entry.advancesTotal)})</span>
                              </div>
                            )}
                            {entry.reason && (
                              <div className="text-xs text-slate-600 italic mt-1">
                                « {entry.reason} »
                              </div>
                            )}
                            <div className="text-[11px] text-slate-500 mt-1">
                              {fmtDateTime(entry.at)} · par <span className="font-medium">{entry.userName || entry.userId || 'inconnu'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
        </DialogFooter>
      </DialogContent>

      <CreatePaymentRequestDialog
        open={showCreatePRDialog}
        onClose={() => setShowCreatePRDialog(false)}
        operationId={operationId}
        onSuccess={() => { fetchWavePRs(); onAdvancesChange?.(); }}
      />
      <NoWaveDialog
        open={showNoWaveDialog}
        onClose={() => setShowNoWaveDialog(false)}
        operationId={operationId}
        onSuccess={() => { fetchWavePRs(); onAdvancesChange?.(); }}
      />
    </Dialog>
  );
};

export default AdvancesManager;
